import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  BLOCK_TYPES,
  BlockedImageResponse,
  BundlesImagesResponse,
  BlockPageQueryParams,
} from './BlockPage.model';
import { environment } from '@/environment';
import { QUERY_PARAMS } from '@/constants/app/routes';
import { STORAGE_KEYS } from '@/constants/storage/storage';
import { localStorageUtils } from '@/utils/storage/localStorage';
import { selectCustomizedBlockPage } from '@/store/selectors';
import { DATA_URL_PREFIX, PNG_BASE64_PREFIX } from '@/constants/ui/images';

const TITLE_KEYS = {
  [BLOCK_TYPES.SITE]: [
    'blockPage.motivationalTexts.text1',
    'blockPage.motivationalTexts.text2',
    'blockPage.motivationalTexts.text3',
  ],
  [BLOCK_TYPES.WORD]: [
    'blockPage.motivationalTexts.text11',
    'blockPage.motivationalTexts.text11',
    'blockPage.motivationalTexts.text11',
  ],
  [BLOCK_TYPES.WORK_MODE]: [
    'blockPage.motivationalTexts.text4',
    'blockPage.motivationalTexts.text5',
    'blockPage.motivationalTexts.text6',
  ],
  [BLOCK_TYPES.WHITE_LIST_WORK_MODE]: [
    'blockPage.motivationalTexts.text4',
    'blockPage.motivationalTexts.text5',
    'blockPage.motivationalTexts.text6',
  ],
  [BLOCK_TYPES.WHITE_LIST_BLOCK_LIST]: [
    'blockPage.motivationalTexts.text12',
    'blockPage.motivationalTexts.text12',
    'blockPage.motivationalTexts.text12',
  ],
  [BLOCK_TYPES.CONTEXT_MENU]: [
    'blockPage.motivationalTexts.text10',
    'blockPage.motivationalTexts.text10',
    'blockPage.motivationalTexts.text10',
  ],
  [BLOCK_TYPES.CONTEXT_MENU_FOCUS_MODE]: [
    'blockPage.motivationalTexts.text10',
    'blockPage.motivationalTexts.text10',
    'blockPage.motivationalTexts.text10',
  ],
} as const;

const MESSAGE_KEYS = {
  [BLOCK_TYPES.SITE]: 'blockPage.messages.siteBlocked',
  [BLOCK_TYPES.WORD]: 'blockPage.messages.wordBlocked',
  [BLOCK_TYPES.WORK_MODE]: 'blockPage.messages.workModeActive',
  [BLOCK_TYPES.WHITE_LIST_WORK_MODE]: 'blockPage.messages.workModeActive',
  [BLOCK_TYPES.WHITE_LIST_BLOCK_LIST]: 'blockPage.messages.whitelistOnly',
  [BLOCK_TYPES.CONTEXT_MENU]: 'blockPage.messages.contextMenuBlocked',
  [BLOCK_TYPES.CONTEXT_MENU_FOCUS_MODE]: 'blockPage.messages.focusModeActive',
} as const;

const CATEGORY_TITLE_KEYS = {
  social: 'blockPage.categoryTitles.social',
  adult: 'blockPage.categoryTitles.adult',
  gambling: 'blockPage.categoryTitles.gambling',
  gaming: 'blockPage.categoryTitles.gaming',
  shopping: 'blockPage.categoryTitles.shopping',
  news: 'blockPage.categoryTitles.news',
  video: 'blockPage.categoryTitles.video',
} as const;

const CATEGORY_MESSAGE_KEYS = {
  social: 'blockPage.categoryMessages.social',
  adult: 'blockPage.categoryMessages.adult',
  gambling: 'blockPage.categoryMessages.gambling',
  gaming: 'blockPage.categoryMessages.gaming',
  shopping: 'blockPage.categoryMessages.shopping',
  news: 'blockPage.categoryMessages.news',
  video: 'blockPage.categoryMessages.video',
} as const;

export const TEXT_COLOR = {
  WHITE: 'white',
  BLACK: 'black',
} as const;

export interface ICustomizedText {
  readonly titleText: string;
  readonly subtitleText: string;
  readonly textColor: string;
}

interface UseBlockPageDataReturn {
  readonly blockType: string | undefined;
  readonly website: string | undefined;
  readonly word: string | undefined;
  readonly category: string | undefined;
  readonly getImageSource: () => string;
  readonly getTitle: () => string;
  readonly getSubtitle: () => string;
  readonly customizedText: ICustomizedText | null;
}

/**
 * Hook for computed block page data from URL params.
 * Contains no UI state - only data derived from URL and localStorage.
 */
export const useBlockPageData = (): UseBlockPageDataReturn => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [dice] = useState<number>(() => Math.floor(Math.random() * 3) + 1);
  const [randBundle, setRandBundle] = useState<BlockedImageResponse | null>(
    null
  );

  const queryParams: BlockPageQueryParams = useMemo(
    () => ({
      word: searchParams.get(QUERY_PARAMS.WORD) ?? undefined,
      blockType: searchParams.get(QUERY_PARAMS.BLOCK_TYPE) ?? undefined,
      category: searchParams.get(QUERY_PARAMS.CATEGORY) ?? undefined,
      site: searchParams.get(QUERY_PARAMS.SITE) ?? undefined,
    }),
    [searchParams]
  );

  const { word, blockType, category } = queryParams;
  const website = queryParams.site;
  const customizedBlockPage = useSelector(selectCustomizedBlockPage);

  const customizedText: ICustomizedText | null =
    customizedBlockPage.isCustomizedText
      ? {
          titleText: customizedBlockPage.titleText,
          subtitleText: customizedBlockPage.subtitleText,
          textColor: customizedBlockPage.textColor,
        }
      : null;

  const getImageSource = useCallback((): string => {
    if (randBundle) {
      return (
        randBundle.resolutions.high ||
        randBundle.resolutions.medium ||
        randBundle.resolutions.low ||
        ''
      );
    }

    if (customizedBlockPage.backgroundImageInByte) {
      const base64 = customizedBlockPage.backgroundImageInByte;
      if (base64.startsWith(DATA_URL_PREFIX)) {
        return base64;
      }
      return `${PNG_BASE64_PREFIX}${base64}`;
    }

    const imagePath = `${environment.baseUrl}/images/blockpage/backgrounds`;

    switch (blockType) {
      case BLOCK_TYPES.SITE:
        return `${imagePath}/BlockSite-00${dice}b.jpg`;
      case BLOCK_TYPES.WHITE_LIST_WORK_MODE:
      case BLOCK_TYPES.WORK_MODE:
        return `${imagePath}/WorkMode-00${dice}b.jpg`;
      case BLOCK_TYPES.WORD:
        return `${imagePath}/WorkMode-002b.jpg`;
      case BLOCK_TYPES.WHITE_LIST_BLOCK_LIST:
        return `${imagePath}/WorkMode-001b.jpg`;
      case BLOCK_TYPES.CATEGORY:
      case BLOCK_TYPES.CONTEXT_MENU:
      case BLOCK_TYPES.CONTEXT_MENU_FOCUS_MODE:
        return `${imagePath}/category-${category}-001b.jpg`;
      default:
        return `${imagePath}/BlockSite-001b.jpg`;
    }
  }, [
    customizedBlockPage.backgroundImageInByte,
    randBundle,
    blockType,
    category,
    dice,
  ]);

  const getSubtitle = useCallback((): string => {
    if (blockType === BLOCK_TYPES.CATEGORY && category) {
      const categoryKey =
        CATEGORY_MESSAGE_KEYS[category as keyof typeof CATEGORY_MESSAGE_KEYS];
      if (categoryKey) {
        return t(categoryKey);
      }
    }

    const messageKey = blockType
      ? MESSAGE_KEYS[blockType as keyof typeof MESSAGE_KEYS]
      : undefined;

    return t(messageKey ?? 'blockPage.messages.siteBlocked');
  }, [blockType, category, t]);

  const getTitle = useCallback((): string => {
    if (randBundle) {
      return randBundle.title;
    }

    if (blockType === BLOCK_TYPES.CATEGORY && category) {
      const categoryKey =
        CATEGORY_TITLE_KEYS[category as keyof typeof CATEGORY_TITLE_KEYS];
      if (categoryKey) {
        return t(categoryKey);
      }
    }

    if (blockType && blockType in TITLE_KEYS) {
      const titleKeys = TITLE_KEYS[blockType as keyof typeof TITLE_KEYS];
      return t(titleKeys[dice - 1]);
    }

    return t('blockPage.motivationalTexts.text1');
  }, [randBundle, blockType, category, dice, t]);

  useEffect(() => {
    const storedBundle =
      localStorageUtils.getObjectItem<BundlesImagesResponse | null>(
        STORAGE_KEYS.BLOCK_PAGE_SELECTED_BUNDLE,
        null
      );

    if (storedBundle) {
      const rand = Math.floor(Math.random() * storedBundle.bundleImages.length);
      setRandBundle(storedBundle.bundleImages[rand]);
    } else {
      setRandBundle(null);
    }
  }, []);

  return {
    blockType,
    website,
    word,
    category,
    getImageSource,
    getTitle,
    getSubtitle,
    customizedText,
  };
};
